<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { IonButton, IonContent, IonIcon, IonList, IonItem, IonRadio, IonLabel, IonPopover } from '@ionic/vue';
import { chevronBackOutline, linkOutline, chevronDownOutline, shareSocialOutline } from 'ionicons/icons';
import { Share } from '@capacitor/share';
import { useI18n } from 'vue-i18n';

import RfidWalletBox from '@/modules/rfid/components/RfidWalletBox.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useRfidGuest from '@/modules/rfid/composables/useRfidGuest';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import useTenant from '@/shared/composables/useTenant';
import router from '@/router';
import useUtils from '@/shared/composables/useUtils';
import useStorageService from '@/shared/composables/useStorageService';
import useToast from '@/shared/composables/useToast';

// Init
const { t, locale } = useI18n();
const assetStore = useAssetsStore();

const route = useRoute();
const encryptedRfidTag = route.params.id?.toString?.() || '';
const isSharedLink = route.query.share === 'true';
const { searchRfidTag, saveGuestWalletBox } = useRfidGuest();
const { isSuccessRfidTag, rfidTag, rfidTagMutate } = searchRfidTag();
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const continueLinking = ref(false);
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const { getAppLanguages } = useUtils();
const selectedAppLanguage = ref('');
const appLanguages = ref(getAppLanguages());
const { set, get } = useStorageService();
const selectedAppLanguageObject = ref();
const { presentToast } = useToast();

// Share wallet function for guest RFID mode
const shareGuestWallet = async () => {
	try {
		if (encryptedRfidTag) {
			await Share.share({
				url: `${window.location.origin}/#/guest-topup/${encryptedRfidTag}`,
			});
		}
	} catch (error: any) {
		if (error && error.message && error.message !== 'Share canceled') {
			await presentToast('top', error, 5000, 'danger');
		}
	}
};

onMounted(async () => {
	if (encryptedRfidTag) {
		rfidTagMutate({ encrypted_rfid_tag: encryptedRfidTag });
	}
	if (route.query.continueLinking) {
		continueLinking.value = true;
	}

	const appLanguage = await get('app_language');

	// Set app language if it exists in storage
	if (appLanguage) {
		selectedAppLanguage.value = appLanguage;
		selectedAppLanguageObject.value = appLanguages.value.find((lang) => lang.value === appLanguage);
	} else {
		// Otherwise, set default app language
		selectedAppLanguage.value = locale.value;
		selectedAppLanguageObject.value = appLanguages.value.find((lang) => lang.value === locale.value);
	}
});

const goTo = async (link: string) => {
	await router.push(link);
};

watch(
	[rfidTag, isSuccessRfidTag],
	async ([tag, success]) => {
		if (tag && typeof tag === 'object' && tag.id !== undefined && success && tenantConfig.value?.enable_full_guest_mode && tenantConfig.value?.enable_guest_mode_full_access) {
			await saveGuestWalletBox(tag, encryptedRfidTag, success, continueLinking.value, true);
			if (tenantConfig.value?.enable_full_guest_mode && tenantConfig.value?.enable_guest_mode_full_access) {
				goTo('/dashboard');
			}
		}
	},
	{ immediate: false },
);

watch(selectedAppLanguage, (newValue) => {
	if (newValue) {
		locale.value = newValue;
		set('app_language', newValue);
		appLanguages.value = getAppLanguages();
		selectedAppLanguageObject.value = appLanguages.value.find(lang => lang.value === newValue);
	}
});
</script>

<template>
	<ion-content>
		<TopBar class="px-[34px]">
			<template #left>
				<div class="flex items-center justify-center">
					<ion-button class="w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full" shape="round">
						<router-link to="/dashboard" class="flex items-center justify-center">
							<ion-icon :icon="chevronBackOutline" class="ion-no-padding ion-no-margin text-black mr-[2px]"></ion-icon>
						</router-link>
					</ion-button>
				</div>
			</template>
			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ isSharedLink ? t('guest_top_up.title') : t('rfid.guest.dashboard') }}
				</div>
			</template>
		</TopBar>

		<div>
			<div class="px-[34px] icon flex justify-center mb-4">
				<img :src="assetStore.logoUrl" class="max-w-[70%]" />
			</div>

			<div v-if="rfidTag && !isSharedLink" class="flex justify-between mb-2 px-[34px] flex">
				<div class="flex items-center">
					<div>
						<router-link :to="`/wallet/history?id=${encryptedRfidTag}`">
							<div class="flex h-[34px] items-center justify-center p-2 bg-white rounded-full">
								<span class="font-sourcesanspro-semibold text-xs2 text-anykrowd-dark">{{ t('components.rfid_wallet_box.history') }}</span>
							</div>
						</router-link>
					</div>
					<div class="ml-2">
						<div class="flex h-[34px] items-center justify-between p-2 bg-white rounded-full">
							<div class="flex items-center mr-2">
								<ion-icon :icon="linkOutline" class="text-anykrowd-dark text-[#707070]"></ion-icon>
							</div>
							<span class="font-sourcesanspro-semibolditalic text-xs2 text-[#707070]">
								{{ t('components.rfid_wallet_box.rfid_linked') }}
							</span>
						</div>
					</div>
					<div v-if="tenantConfig?.guest_wallet_topup_clientx" class="ml-2">
						<div class="flex h-[34px] items-center justify-between p-2 bg-white rounded-full cursor-pointer" @click="shareGuestWallet()">
							<div class="flex items-center mr-2">
								<ion-icon :icon="shareSocialOutline" class="text-anykrowd-dark text-[#707070]"></ion-icon>
							</div>
							<span class="font-sourcesanspro-semibold text-xs2 text-anykrowd-dark">
								{{ t('guest_top_up.share.title') }}
							</span>
						</div>
					</div>
				</div>
				<div>
					<div id="popover-button" class="flex items-center justify-center p-2 rounded-full font-sourcesanspro-bold text-xs2 cursor-pointer bg-white">
						<ion-label class="flex items-center">
							<div :class="selectedAppLanguageObject?.flagClass" class="mr-2" style="width: 1.5em; height: 1em"></div>
							<div class="mr-2 text-black font-sourcesanspro-regular">{{ t(selectedAppLanguageObject?.label) }}</div>
							<ion-icon class="text-anykrowd-dark" :icon="chevronDownOutline"></ion-icon>
						</ion-label>
					</div>
					<ion-popover trigger="popover-button" :dismiss-on-select="true">
						<ion-content>
							<ion-list>
								<ion-item v-for="language in appLanguages" :key="language.value" class="text-white font-sourcesanspro-regular cursor-pointer" lines="none" :class="{ 'selected': selectedAppLanguage === language.value }" @click="selectedAppLanguage = language.value">
									<ion-radio :value="language.value" :checked="selectedAppLanguage === language.value" class="mr-4"></ion-radio>
									<ion-label class="flex items-center">
										<span :class="language.flagClass" class="mr-2" style="width: 1.5em; height: 1em"></span>
										<span class="text-white font-sourcesanspro-regular">{{ t(language.label) }}</span>
									</ion-label>
								</ion-item>
							</ion-list>
						</ion-content>
					</ion-popover>
				</div>
			</div>
			<RfidWalletBox :encryptedRfidTag="encryptedRfidTag" :isSuccessRfidTag="isSuccessRfidTag" :rfidTag="rfidTag" :user-profile="userProfile" :continue-linking="continueLinking" :is-shared-link="isSharedLink"></RfidWalletBox>
		</div>
	</ion-content>
</template>

<style scoped></style>
